import {Divider, Stack} from '@mui/material';
import React, {Fragment} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';

import {BillData} from 'domain/usecases';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {MainFooter, StyledCard, StyledGrid, AlertInfo} from 'presentation/components';

import {MyPaymentsEnum, OpenBillsTestIdEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useSelectedBilletStore} from '../../store/selectedBilletStore';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';

import {SaoLucasRouteState} from './types';
import {Container} from '../../styles';
import {OlderBillsModal} from '../components/OlderBillsModal';
import {useOlderBillsCheck} from '../hooks/useOlderBillsCheck';
import {useOpenBills} from '../open-bills/hooks/useOpenBills';
import {BarCodeInfo} from './components/BarCodeInfo';
import {DataInfoBillet} from './components/DataInfoBillet';
import {ExpiredDataInfo} from './components/ExpiredDataInfo';
import {HeaderCard} from './components/HeaderCard';
import {NegotiationDataInfo} from './components/NegotiationDataInfo';
import {PriceInfoBillet} from './components/PriceInfoBillet';
import {TitleAndDownloadButton} from './components/TitleAndDownloadButton';
import {useBilletDetails} from './hooks/useBilletDetails';

const {
	beneficiaries,
	competenceOfThisMonthly,
	contractNumber,
	dueDate,
	paymentDate,
	paymentInformation,
	alertWarning,
	attention,
} = translations['pt-br'].privateFlow.myFinancial.billetDetails;

function BilletDetailsPage() {
	const {isMobile} = useMediaQueries();
	const navigate = useNavigate();
	const {state: routeState} = useLocation();
	const saoLucasRouteState = routeState as SaoLucasRouteState | null;
	const isSaoLucasBill = !!saoLucasRouteState?.isSaoLucasBill;
	const saoLucasDownloadLink = saoLucasRouteState?.downloadLink;

	const {accesses} = useAccountDataStore(store => store.accountData);
	const {flSaoLucasSaude} = accesses;

	const selectedCurrentBillet = useSelectedBilletStore(state => state.selectedBillet);
	const setSelectedCurrentBillet = useSelectedBilletStore(state => state.setSelectedBillet);
	const flMigracao = selectedCurrentBillet?.flMigracao ?? false;

	let effectiveDownloadLink: string | undefined;
	if (flMigracao && isSaoLucasBill) {
		effectiveDownloadLink = saoLucasDownloadLink;
	} else if (flMigracao && flSaoLucasSaude) {
		effectiveDownloadLink = selectedCurrentBillet?.urlBoletoMigrado ?? undefined;
	}

	const {
		beneficiary,
		beneficiariesLength,
		isInNegotiation,
		isExpired,
		isPaid,
		competenceOfThisMonthlyFee,
		linhaDigitavel,
		handleDownload,
		isDownloading,
		modalErrorState,
		setModalErrorState,
	} = useBilletDetails({downloadLink: effectiveDownloadLink});

	const isOpen = !isInNegotiation && !isPaid && !isExpired;

	const {
		checkOlderBills,
		numberOfBills,
		mostOldBill,
		currentBill,
		isModalOpen,
		handleCloseModal,
		actionType,
	} = useOlderBillsCheck();

	const {mergedBills} = useOpenBills();

	const handleGoToBillDetails = (bill: BillData) => {
		handleCloseModal();
		const mergedBill = mergedBills.find(m => m.bill.cdObrigacao === bill.cdObrigacao);
		const billToNavigate = mergedBill?.bill ?? bill;
		const billDownloadLink = mergedBill?.downloadLink;
		setSelectedCurrentBillet(billToNavigate);
		if (billDownloadLink) {
			navigate(MyPaymentsEnum.BILLET_DETAILS, {
				state: {isSaoLucasBill: true, downloadLink: billDownloadLink},
			});
			return;
		}
		navigate(MyPaymentsEnum.BILLET_DETAILS, {state: null});
	};

	if (!selectedCurrentBillet) {
		return <Navigate to={MyPaymentsEnum.OPEN_BILLS} />;
	}

	const shouldShowLatePaymentAlert =
		flMigracao && isOpen && !isSaoLucasBill && !selectedCurrentBillet?.urlBoletoMigrado;

	return (
		<Fragment>
			<OlderBillsModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				numberOfBills={numberOfBills}
				mostOldBill={mostOldBill}
				currentBill={currentBill}
				onGoToBillDetails={handleGoToBillDetails}
				onDownload={actionType === 'details' ? handleDownload : undefined}
				actionType={actionType === false ? undefined : actionType}
			/>
			<Container>
				<TitleAndDownloadButton
					onCheckOlderBills={() => checkOlderBills(selectedCurrentBillet, 'details')}
					downloadLink={effectiveDownloadLink}
					hideWhenNoLink={flSaoLucasSaude && !isSaoLucasBill && flMigracao}
					downloadDataTagId={
						isSaoLucasBill
							? BillsTaggingEnum.CLICK_BAIXAR_PDF_BOLETO_SAO_LUCAS
							: undefined
					}
					downloadTestId={
						isSaoLucasBill
							? OpenBillsTestIdEnum.BTN_BAIXAR_PDF_BOLETO_SAO_LUCAS
							: OpenBillsTestIdEnum.BTN_BAIXAR_PDF_BOLETO
					}
					isDownloading={isDownloading}
					isPaid={isPaid}
					modalErrorState={modalErrorState}
					setModalErrorState={setModalErrorState}
					onDownload={handleDownload}
				/>
				<HeaderCard />

				<StyledCard.Container>
					<Stack gap={'16px'}>
						<DataInfoBillet title={paymentInformation} />

						{!isSaoLucasBill ? (
							<Fragment>
								<StyledGrid.Root>
									<StyledGrid.Item xs={6}>
										<DataInfoBillet
											title={contractNumber}
											subtitle={
												beneficiary.nuContratoC ?? beneficiary.nuContrato
											}
										/>
									</StyledGrid.Item>
									<StyledGrid.Item xs={6}>
										<DataInfoBillet
											title={beneficiaries}
											subtitle={beneficiariesLength.toString()}
										/>
									</StyledGrid.Item>
									<StyledGrid.Item xs={12}>
										{isMobile ? (
											<Stack mb={'16px'}>
												<Divider />
											</Stack>
										) : null}
										<DataInfoBillet
											title={competenceOfThisMonthly}
											subtitle={competenceOfThisMonthlyFee}
										/>
									</StyledGrid.Item>
								</StyledGrid.Root>
								<Divider />
							</Fragment>
						) : null}

						<StyledGrid.Root>
							<StyledGrid.Item sm={4} xs={6}>
								<DataInfoBillet
									title={dueDate}
									subtitle={linhaDigitavel?.dtVencimento ?? '-'}
									customSubtitleFontSize={isMobile ? '1.25rem' : '1.5rem'}
									customSubtitleFontWeight={isMobile ? 600 : 400}
								/>
							</StyledGrid.Item>
							<StyledGrid.Item sm={4} xs={6}>
								<PriceInfoBillet />
							</StyledGrid.Item>
							{isPaid ? (
								<StyledGrid.Item sm={4} xs={6}>
									<DataInfoBillet
										title={paymentDate}
										subtitle={linhaDigitavel?.dtPagamento ?? '-'}
										customSubtitleFontSize={isMobile ? '1.25rem' : '1.5rem'}
										customSubtitleFontWeight={isMobile ? 600 : 400}
									/>
								</StyledGrid.Item>
							) : null}
						</StyledGrid.Root>

						{isInNegotiation ? (
							<NegotiationDataInfo />
						) : (
							<BarCodeInfo
								onCheckOlderBills={() =>
									checkOlderBills(selectedCurrentBillet, 'copy')
								}
								copyTestId={OpenBillsTestIdEnum.BTN_COPIAR_CODIGO_BOLETO_DETALHES}
							/>
						)}

						<ExpiredDataInfo />
						{shouldShowLatePaymentAlert ? (
							<AlertInfo.Root severity="warning">
								<AlertInfo.Title title={attention} />
								<AlertInfo.Description>{alertWarning}</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}
					</Stack>
				</StyledCard.Container>
			</Container>
			<MainFooter customMarginBottom={isMobile ? '128px' : undefined} />
		</Fragment>
	);
}

export {BilletDetailsPage};
